
/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import React from "react";
import { formatDigits } from "../../Utils/formatNumbers";
import { dateStringTimeConvert } from "../../Utils/titleCaseFunction";


const container = css`
  width: 80mm;
  padding: 6mm;
  font-family: 'Courier New', monospace;
  font-size: 12pt;
  color: #111;
  background-color: #fff;
  box-sizing: border-box;
  margin: 0 auto;

  @media print {
    @page {
      size: 80mm auto;
      margin: 0;
    }
    width: 80mm;
    padding: 6mm;
  }
`;

const header = css`
  text-align: center;
  margin-bottom: 12px;

  img {
    display: block;
    margin: 0 auto 6px auto;
    max-width: 60mm;
    height: auto;
  }

  h1 {
    font-size: 15pt;
    margin: 0 0 6px 0;
    font-weight: bold;
    color: #000;
  }

  p {
    margin: 4px 0;
    font-size: 11pt;
    color: #222;
  }

  hr {
    border: 0;
    border-top: 1px dashed #000;
    margin: 6px 0;
  }
`;

const table = css`
  width: 100%;
  border-collapse: collapse;
  font-size: 11pt;
  margin-bottom: 8px; /* add spacing below table */

  th, td {
    border: 1px solid #000;
    padding: 6px 4px; /* increase padding for bigger numbers */
    color: #111;
  }

  th {
    font-weight: bold;
    background-color: #f2f2f2;
    font-size: 11pt;
    text-align: center;
  }

  td.product-name {
    width: 50%;
    text-align: left;
    white-space: normal;
    word-break: break-word;
  }

  td.qty, td.rate, td.total {
    width: 16%;
    text-align: right;
    white-space: nowrap;
    min-width: 40px; /* ensure numbers have space */
  }

  tr.total-row td {
    font-weight: bold;
    background-color: #e8e8e8;
    border-top: 2px solid #000;
    padding: 8px 4px; /* more padding for totals */
    font-size: 11pt;
  }

  tr.total-row td:last-child {
    text-align: right; /* ensure totals align right */
  }

  tr:last-child td {
    border-bottom: 2px solid #000;
  }
`;

const footer = css`
  text-align: center;
  font-size: 11pt;
  margin-top: 16px; /* more spacing above footer */

  hr {
    border: 0;
    border-top: 1px dashed #000;
    margin: 8px 0;
  }

  .received-by {
    margin-top: 8px;
    font-weight: bold;
    font-size: 12pt;
    text-align: left;
    color: #111;
  }
`;



const ThermalReceipt = React.forwardRef(({ company, customer, products }, ref) => {
  const user_name = `${customer.first_name || ""} `.trim();

  return (
    <div css={container} ref={ref}>
      {/* Header */}
      <div css={header}>
        <h1>{company.company_name || "VIP PHARMACY"}</h1>
        <p>{company.address || ""}</p>
        <p><strong>{company.mobile || ""}</strong></p>
        <hr />
        <p>Receipt: <strong>{customer.receipt_number || ""}</strong> </p>
        <p>Date:<strong> {customer.date ? dateStringTimeConvert(customer.date) : ""}</strong> </p>
        <hr />
        <p><strong>Received From:</strong> <strong>{customer.customer_name || ""}</strong></p>
      </div>

      {/* Products Table */}
      <table css={table}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Qty</th>
            <th>Rate</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) => {
            const qty = p.quantity || 0;
            const rate = p.rate || 0;
            const total = qty * rate;
            return (
              <tr key={i}>
                <td className="product-name">{p.product_name}</td>
                <td className="qty">{qty}</td>
                <td className="unitPrice rate">{formatDigits(rate)}</td>
                <td className="amount total">{formatDigits(total)}</td>
              </tr>
            );
          })}

          {/* Totals */}
          <tr className="total-row">
            <td colSpan="3" style={{ textAlign: "right" }}>Total Amount:</td>
            <td className="amount">{formatDigits(customer.total_amount)}</td>
          </tr>
          <tr className="total-row">
            <td colSpan="3" style={{ textAlign: "right" }}>Amount Received:</td>
            <td className="amount">{formatDigits((customer.amount_paid || 0) + (customer.balance || 0))}</td>
          </tr>
          <tr className="total-row">
            <td colSpan="3" style={{ textAlign: "right" }}>Change:</td>
            <td className="amount">{formatDigits(customer.balance || 0)}</td>
          </tr>
          <tr className="total-row">
            <td colSpan="3" style={{ textAlign: "right" }}>Total Discount:</td>
            <td className="amount">{formatDigits(customer.total_discount || 0)}</td>
          </tr>
        </tbody>
      </table>

      {/* Footer */}
      <div css={footer}>
        <hr />
        <p>Thank you for shopping with us!</p>
        {user_name && <div className="received-by">Received By: {user_name}</div>}
      </div>
    </div>
  );
});

export default ThermalReceipt;
